import React, { useEffect, useId, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { isJapanTraffic } from '../../../_helpers/japanTraffic.js';
import { useLyricsImpression } from '../../../hooks/useLyricsImpression.js';
import LyricsAttribution from '../lyrics/LyricsAttribution.jsx';
import '../../../styles/LiveMomentCard.css';

const LETTERS = ['A', 'B', 'C', 'D'];

/** What stands in for the missing word of a Finish the lyric line. */
export const LYRIC_BLANK = '____';

/** Cancels copying the provider's words (ENG-521). */
const preventCopying = (event) => event.preventDefault();

/** A lyric line with its blank drawn as a gap rather than underscores. */
const LyricLine = ({ line, label }) => {
  const at = line.lastIndexOf(LYRIC_BLANK);
  if (at < 0) return line;
  return (
    <>
      {line.slice(0, at)}
      <span className="live-moment-blank" aria-label={label} />
      {line.slice(at + LYRIC_BLANK.length)}
    </>
  );
};

/** Whole seconds left until endsAt on the server's clock. */
const secondsLeft = (endsAt, offsetMs) => {
  const end = Date.parse(endsAt);
  if (!Number.isFinite(end)) return 0;
  return Math.max(0, Math.ceil((end - (Date.now() + offsetMs)) / 1000));
};

/**
 * The LIVE MOMENT card (mockups 12–13, ENG-689/691) in the performer card's
 * place while a moment runs: the label ("ARTIST QUIZ · CHRIS STAPLETON"),
 * the question, options A–D, a countdown bar with "20s left · 31 answered"
 * and a close X. One answer per listener, locked once chosen. At the end the
 * right option lights up, the listener's own is marked right or wrong, and
 * the room's "% got it right" shows.
 *
 * Finish the lyric (mockup 12, ENG-689) is the same card with
 * "FINISH THE LYRIC · {song}", a line of the song with its last word as a
 * gap, and words to pick. When the words are Musixmatch's they carry the
 * credit and the impression every lyrics surface owes
 * (docs/musixmatch-display-obligations.md), and can't be copied from Japan.
 */
const LiveMomentCard = ({ moment, chosen, result, answered, error, offsetMs = 0, onAnswer, onDismiss }) => {
  const { t, format } = useLocale();
  const headingId = useId();
  const [left, setLeft] = useState(() => (moment ? secondsLeft(moment.endsAt, offsetMs) : 0));
  const attribution = moment?.kind === 'lyric' ? moment.attribution ?? null : null;
  useLyricsImpression(attribution?.scriptTrackingUrl ?? null, { pixelUrl: attribution?.pixelTrackingUrl ?? null });

  useEffect(() => {
    if (!moment || result) return undefined;
    setLeft(secondsLeft(moment.endsAt, offsetMs));
    const timer = setInterval(() => setLeft(secondsLeft(moment.endsAt, offsetMs)), 250);
    return () => clearInterval(timer);
  }, [moment, offsetMs, result]);

  if (!moment) return null;
  const total = Math.max(1, Math.round((Date.parse(moment.endsAt) - Date.parse(moment.startsAt)) / 1000) || 20);
  const progress = result ? 0 : Math.min(1, left / total);
  const isLyric = moment.kind === 'lyric';
  let label;
  if (isLyric) {
    label = moment.songTitle
      ? t('stream.moment.lyricLabel', { song: moment.songTitle })
      : t('stream.moment.lyricLabelPlain');
  } else {
    label = moment.artist
      ? t('stream.moment.quizLabel', { artist: moment.artist })
      : t('stream.moment.quizLabelPlain');
  }
  const blockCopying = Boolean(attribution) && isJapanTraffic();
  const copyGuard = blockCopying
    ? { onCopy: preventCopying, onCut: preventCopying, onDragStart: preventCopying, onContextMenu: preventCopying }
    : {};

  const stateOf = (index) => {
    if (!result) return chosen === index ? 'chosen' : '';
    if (index === result.correctOption) return 'right';
    if (index === chosen) return 'wrong';
    return 'faded';
  };

  let status;
  if (result) {
    let mine = '';
    if (chosen === result.correctOption) mine = t('stream.moment.youRight');
    else if (chosen != null) mine = t('stream.moment.youWrong');
    const room = t('stream.moment.room', { percent: format.number(result.percentRight) });
    status = mine ? t('stream.moment.resultLine', { mine, room }) : room;
  } else {
    status = t('stream.moment.countdown', {
      seconds: format.number(left),
      count: answered,
      formattedCount: format.number(answered),
    });
  }

  return (
    <section className="live-moment" data-testid="live-moment-card" aria-labelledby={headingId}>
      <header className="live-moment-header">
        <span className="live-moment-kicker">{t('stream.moment.kicker')}</span>
        <button type="button" className="live-moment-close" onClick={onDismiss} aria-label={t('stream.moment.close')} data-testid="live-moment-close">
          <FaTimes aria-hidden="true" />
        </button>
      </header>
      <span className="live-moment-label">{label}</span>
      <div className={blockCopying ? 'live-moment-words live-moment-words--no-copy' : 'live-moment-words'} {...copyGuard}>
      <h2 className={`live-moment-question${isLyric ? ' live-moment-question--lyric' : ''}`} id={headingId}>
        {isLyric ? <LyricLine line={moment.question} label={t('stream.moment.blank')} /> : moment.question}
      </h2>
      <ol className="live-moment-options" aria-label={t('stream.moment.optionsLabel')}>
        {moment.options.map((option, index) => (
          <li key={option}>
            <button
              type="button"
              className={`live-moment-option ${stateOf(index) ? `live-moment-option--${stateOf(index)}` : ''}`}
              onClick={() => onAnswer(index)}
              disabled={chosen != null || Boolean(result) || left === 0}
              aria-pressed={chosen === index}
              data-testid="live-moment-option"
            >
              <span className="live-moment-letter" aria-hidden="true">{LETTERS[index]}</span>
              <span>{option}</span>
            </button>
          </li>
        ))}
      </ol>
      </div>
      {attribution && (
        <LyricsAttribution copyright={attribution.copyright} backlinkUrl={attribution.backlinkUrl} variant="live-moment" />
      )}
      <div className="live-moment-bar" aria-hidden="true">
        <span className="live-moment-bar-fill" style={{ inlineSize: `${progress * 100}%` }} />
      </div>
      <p className="live-moment-status" role="status" aria-live="polite" data-testid="live-moment-status">{status}</p>
      {error && error !== 'moment_already_answered' && (
        <p className="live-moment-error" role="alert">
          {t(error === 'moment_closed' ? 'stream.moment.closed' : 'stream.moment.failed')}
        </p>
      )}
    </section>
  );
};

export default LiveMomentCard;
